import {
  Badge,
  Box,
  BoxProps,
  Icon,
  IconButton,
  ImageProps,
  Spinner,
  useToast,
  Text,
} from '@chakra-ui/react';
import { ImageVariant, NestedHome, NestedUser, TODO } from '@habity/types';
import { ICONS } from '~/constants/icons';
import { Gallery } from '../images/Gallery';
import { ProfileModalType } from '@habity/types';
import { isAddedToWishlist } from '@habity/services';
import { OverlayButton } from '../images/buttons/OverlayButton';
import { useWishlist } from '~/hooks/useWishlist';
import { ProfileButtons } from '../images/buttons/Profile';
import { useState, useEffect } from 'react';
import { useAppStore } from '~/store';
import { Link } from '@chakra-ui/next-js';
import { HomeProfileOverlayBadges } from '~/components/homes/profile/components/OverlayBadges';
import { useInView } from 'react-intersection-observer';

interface ImageCardProps {
  // TODO: Replace with HomeCardData
  home: NestedHome;
  user?: NestedUser;
  isLoggedIn?: boolean;
  href?: string;
  boxProps?: BoxProps;
  imageVariant: ImageVariant;
  onClick?: () => void;
  onGalleryModalOpen?: () => void;
  aspectRatio?: number;
  borderTopRadius?: TODO;
  borderBottomRadius?: TODO;
  imageProps?: ImageProps;
  bottomRightCTA?: boolean;
  maxSlides?: number;
  handleOpen?: (params: ProfileModalType) => void;
  pagination: TODO;
  isLoading?: boolean;
  addedToWishlist?: boolean;
  actions?: boolean;
  allowActions?: boolean;
  showStatusBadge?: boolean;
  showInspectedBadge?: boolean;
  showNextViewingBadge?: boolean;
  showBiddingBadge?: boolean;
  showDebugBadge?: boolean;
  spacing?: 'default' | 'compact';
}

export const HomeImageCard = ({
  home,
  user,
  isLoggedIn = false,
  href,
  boxProps,
  imageVariant,
  onClick,
  onGalleryModalOpen,
  aspectRatio,
  borderTopRadius,
  borderBottomRadius,
  imageProps,
  maxSlides,
  handleOpen,
  pagination,
  isLoading = false,
  addedToWishlist = false,
  allowActions = true,
  showStatusBadge = true,
  showInspectedBadge = false,
  showNextViewingBadge = false,
  showBiddingBadge = true,
  showDebugBadge = false,
}: ImageCardProps) => {
  const {
    addToWishlist,
    removeFromWishlist,
    isAddingToWishlist,
  } = useWishlist();

  const toast = useToast();
  const isMobile = useAppStore((state) => state.isMobile);

  // Track wishlist loading state for remove action only
  const [isRemovingFromWishlist, setIsRemovingFromWishlist] =
    useState<boolean>(false);

  // Track if home is in wishlist
  const [isInWishlist, setIsInWishlist] = useState<boolean>(
    user ? isAddedToWishlist(user, home.id) || addedToWishlist : false
  );

  // Update wishlist state when user or addedToWishlist props change
  useEffect(() => {
    if (user) {
      setIsInWishlist(isAddedToWishlist(user, home.id) || addedToWishlist);
    } else {
      setIsInWishlist(false);
    }
  }, [user, home.id, addedToWishlist]);

  // Handle toggling wishlist status
  const handleWishlistToggle = async (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();

    if (isAddingToWishlist || isRemovingFromWishlist) return;

    if (isInWishlist) {
      // Remove from wishlist - keep optimistic update for remove action
      const wasInWishlist = isInWishlist;
      setIsInWishlist(false);
      setIsRemovingFromWishlist(true);

      try {
        await removeFromWishlist(home);
      } catch (error) {
        console.error('Error removing from wishlist:', error);
        // Revert the optimistic update if there was an error
        setIsInWishlist(wasInWishlist);

        if (!isMobile)
          toast({
            title: 'Något gick fel',
            description: 'Försök igen senare',
            status: 'error',
            position: 'bottom',
          });
      } finally {
        setIsRemovingFromWishlist(false);
      }
    } else {
      // Add to wishlist - the hook handles everything (login check, API call, confirmation modal)
      await addToWishlist(home);
    }
  };

  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0,
  });

  const gallery = inView ? (
    <Gallery
      variant="home"
      images={home.images}
      imageVariant={imageVariant}
      onClick={onGalleryModalOpen}
      aspectRatio={aspectRatio}
      borderTopRadius={borderTopRadius}
      borderBottomRadius={borderBottomRadius}
      imageProps={imageProps}
      maxSlides={maxSlides}
      pagination={pagination}
      isLoading={isLoading}
      isBlurred={!user && imageVariant !== "profile" && home?.status?.show_public_preview}
    />
  ) : null;

  return (
    <Box
      ref={ref}
      position="relative"
      className="group"
      {...boxProps}
      onClick={onClick}
      backgroundColor="blackAlpha.50"
      minH="300px"
    >
      {/* Gallery: Allow nav when loaded */}
      {!href || isLoading ? (
        gallery
      ) : (
        <Link href={href} _hover={{ textDecoration: 'none' }}>
          {gallery}
        </Link>
      )}

      {/* Overlay badges */}
      <Box position="absolute" top={3} left={3} zIndex="1">
        <HomeProfileOverlayBadges
          home={home}
          showStatusBadge={showStatusBadge}
          showInspectedBadge={showInspectedBadge}
          showNextViewingBadge={showNextViewingBadge}
          showBiddingBadge={showBiddingBadge}
          showPremarketHomesLocked={!isLoggedIn || maxSlides === 1}
          showDebugBadge={showDebugBadge}
        />
      </Box>

      {/* Debug badge. USE CAREFULLY */}
      {showDebugBadge && (
        <Box position="absolute" bottom={3} left={3} zIndex="1">
          <Box>
            <Badge variant="info">
              <Text size="sm" fontWeight="medium">
                {home.id}
              </Text>
            </Badge>
          </Box>
        </Box>
      )}

      {/* Overlay gallery modal button */}
      {imageVariant === 'profile' && (
        <Box position="absolute" top={3} right={3} zIndex="1">
          <IconButton
            icon={<ICONS.EXPAND />}
            aria-label="open-gallery"
            backgroundColor="blackAlpha.300"
            onClick={onGalleryModalOpen}
          />
        </Box>
      )}

      {/* Overlay button: Wishlist */}
      {imageVariant !== 'profile' && (
        <Box position="absolute" top="2" right="2">
          {isLoading || !allowActions ? null : (
            <OverlayButton
              label={
                isInWishlist
                  ? `${home.id} är sparad på önskelistan`
                  : `Spara ${home.id} till dina önskelistor`
              }
              onClick={handleWishlistToggle}
              icon={
                isAddingToWishlist || isRemovingFromWishlist ? (
                  <Spinner size="sm" color="black" />
                ) : (
                  <Icon
                    transition="transform 0.2s"
                    _hover={{ transform: 'scale(1.2)' }}
                    color="white"
                    as={isInWishlist ? ICONS.WISHLIST_ACTIVE : ICONS.WISHLIST}
                    boxSize="6"
                    pt="2px"
                  />
                )
              }
              buttonProps={{
                rounded: 'full',
                'aria-label': 'Önskelista',
                backgroundColor: 'transparent',
                _hover: {
                  backgroundColor: 'transparent',
                },
                px: '1.5',
                py: '4.5',
              }}
            />
          )}
        </Box>
      )}

      {/* TODO: Move the inner buttons out here with the rest */}
      {imageVariant !== 'profile' && !isLoading && handleOpen && (
        <ProfileButtons home={home} handleOpen={handleOpen} />
      )}
    </Box>
  );
};
